import React, { useEffect, useState, useRef } from 'react';
import { AxiosPromise } from 'axios';
import { ServerResponse } from 'data/types'
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import {
  FormLayoutContent,
  FormLayoutFooter,
  FormLayout,
} from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import AlarmNotificationProfileForm from 'routes/AlarmNotificationProfile/components/AlarmNotificationProfileForm';
import { Ref } from 'routes/AlarmNotificationProfile/components/AlarmNotificationProfileForm/AlarmNotificationProfileForm';
import { RootState } from 'data/root.reducers';
import {
  SelectionChangedEvent,
  IServerSideGetRowsParams,
  RowSelectedEvent
} from '@ag-grid-enterprise/all-modules';
import { getSelectedDeliveryFunctionId } from 'global/global-selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as alarmNotificationProfileSelectors from 'data/alarmNotificationProfile/alarmNotificationProfile.selectors';
import * as alarmNotificationProfileActions from 'data/alarmNotificationProfile/alarmNotificationProfile.actions';
import * as alarmNotificationApi from 'data/alarmNotificationProfile/alarmNotificationProfile.api';
import ServerSideDatasource from 'shared/commonGrid/serverSideDatasource';
import { showError } from 'shared/toaster'
import {
  AlarmNotificationProfile, AdaptedAlarmNotificationProfile, AlarmsDef, AlarmsInst, InterceptAlarm
} from 'data/alarmNotificationProfile/alarmNotificationProfile.types';
import { FieldUpdateHandlerParam } from 'shared/form';
import { newAlarmNotificationProfile } from 'routes/AlarmNotificationProfile/AlarmNotificationProfile.types';
import { FormTemplate } from 'data/template/template.types';
import { useAlarmNotificationProfileFieldCreateNew } from '../../hook/useAlarmNotificationProfileFieldCreateNew';
import { alarmNotificationProfileTemplate } from '../../templates';
import { adaptTemplateVisibilityMode, addIdToAlarmDef } from '../../utils/alarmNotificationProfileTemplate.adapter';
import { getAuditDetailsToSubmit } from '../../utils/alarmNotificationProfileTemplateDataToSubmit';

type RouterParams = {
  history: string;
};
const mapState = (state: RootState) => ({
  alarmNotificationProfileTemplateDef: adaptTemplateVisibilityMode(
    alarmNotificationProfileTemplate,
    'create',
  ),
  userId: state.authentication.userId,
  alarmNotificationProfileTemplate: adaptTemplateVisibilityMode(alarmNotificationProfileTemplate, 'create'),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  deliveryFunctionId: getSelectedDeliveryFunctionId(state),
  interceptAlarmList: alarmNotificationProfileSelectors.getInterceptAlarmList(state),
  alarmsInstList: alarmNotificationProfileSelectors.getAlarmsInstList(state),
  alarmsDefList: alarmNotificationProfileSelectors.getAlarmsDefList(state),
});

const mapDispatch = {
  createAlarmNotificationProfile: alarmNotificationProfileActions.createAlarmNotificationProfile,
  goToAlarmNotificationProfileList: alarmNotificationProfileActions.goToAlarmNotificationProfileList,
  getInterceptAlarmList: alarmNotificationProfileActions.getInterceptAlarmList,
  getAlarmsInstList: alarmNotificationProfileActions.getAlarmsInstList,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>;
export type Props = PropsFromRedux & PropsFromRouter;

export const AlarmNotificationProfileCreate = ({
  history,
  userId,
  goToAlarmNotificationProfileList,
  createAlarmNotificationProfile,
  userName,
  isAuditEnabled,
  alarmNotificationProfileTemplateDef,
  deliveryFunctionId,
  interceptAlarmList,
  alarmsInstList,
  alarmsDefList,
  getInterceptAlarmList,
  getAlarmsInstList,
}: Props): JSX.Element => {
  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<AdaptedAlarmNotificationProfile>(
    cloneDeep(newAlarmNotificationProfile),
  );
  const [currentContentType, setCurrentContentType] = useState<string>('');
  const [selectedRows, setSelectedRows] = useState<(InterceptAlarm | AlarmsInst | AlarmsDef)[]>([]);
  const [formGridData, setFormGridData] = useState<any[] | null>([]);
  const [formGridDefaultSelections, setFormGridDefaultSelections] = useState<any[] | null>(null);
  const [template, setTemplate] = useState(alarmNotificationProfileTemplateDef);
  const [handleFieldCreateNew] = useAlarmNotificationProfileFieldCreateNew(
    (param) => handleFieldUpdate(param),
  );
  const [field, setField] = useState<string>('');
  const { t } = useTranslation();

  useEffect(() => {
    if (template && formData) {
      const clonedFormData = cloneDeep(formData as AdaptedAlarmNotificationProfile);
      setFormData(clonedFormData);
      setCurrentContentType('ALARM_INSTANCE');
    }
  }, []);

  useEffect(() => {
    // getInterceptAlarmList();
    getAlarmsInstList();
  }, [
    getInterceptAlarmList,
    getAlarmsInstList,
  ])

  // useEffect(() => {
  //   if (template && alarmsDefList) {
  //     const clonedTemplate: FormTemplate | undefined = cloneDeep(template);
  //     clonedTemplate.formId = Date.now().toString();
  //     setTemplate(clonedTemplate);
  //     setCurrentContentType('ALARM_DEFINITION');
  //     setFormGridData(addIdToAlarmDef(['alarmGrpId', 'module'], alarmsDefList));
  //   }
  // }, [
  //   alarmsDefList,
  // ]);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (template == null || formData == null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as AdaptedAlarmNotificationProfile);

    if (fieldName === 'profile_type') {
      setCurrentContentType(value);
      if (value === 'ALARM_DEFINITION') {
        setFormGridData(addIdToAlarmDef(['alarmId'], alarmsDefList));
        setSelectedRows(formData?.alarmsDef || []);
      } else if (value === 'ALARM_INSTANCE') {
        setFormGridData([]);
        setSelectedRows(formData?.alarmsInst || []);
      } else if (value === 'INTERCEPT_ALARM') {
        setFormGridData([]);
        setFormGridDefaultSelections(formData?.interceptAlarm || []);
        setSelectedRows(formData?.interceptAlarm || []);
      }
    }

    const newData = {
      ...clonedFormData,
      [fieldName]: value,
    };

    if (fieldName !== field) {
      setField(fieldName);
    }

    setFormData(newData);
  };

  const handleSelectionChanged = (event: RowSelectedEvent) => {
    let allSelectedRow = cloneDeep(selectedRows);
    const isSelected = event.node.isSelected();
    if (isSelected) {
      allSelectedRow.push(event.data)
    } else {
      allSelectedRow = allSelectedRow.filter((item) => item.id !== event.data.id)
    }
    setSelectedRows(allSelectedRow);
  };

  const getCreateAlarmNotificationProfileActionPayload = () => {
    if (formRef.current === undefined || formRef.current === null) return null;
    if (!formRef.current.isValid(true, true)) return null;

    const alarmNotificationProfileData = formRef.current.getValues();
    if (!alarmNotificationProfileData) return null;

    const profileType = alarmNotificationProfileData.profile_type;
    const alarmsDefSelected: string = profileType === 'ALARM_DEFINITION' ?
      (selectedRows as AlarmsDef[]).reduce((resultString, alarmDef, index) => {
        return index === 0 ? alarmDef.alarmId : `${resultString},${alarmDef.alarmId}`;
      }, '') : '';
    const alarmsInstSelected: string = profileType === 'ALARM_INSTANCE' ?
      (selectedRows as AlarmsInst[]).reduce((resultString, alarmInst, index) => {
        const valueString = `${alarmInst.alarmid}|${alarmInst.instance}`;
        return index === 0 ? valueString : `${resultString},${valueString}`;
      }, '') : '';
    const interceptAlarmSelected: string = profileType === 'INTERCEPT_ALARM' ?
      (selectedRows as InterceptAlarm[]).reduce((resultString, interceptAlarm, index) => {
        return index === 0 ? interceptAlarm.survIndex : `${resultString},${interceptAlarm.survIndex}`;
      }, '') : '';

    const adaptedAlarmNotificationProfileData: AlarmNotificationProfile = {
      alarmNtfyProfileId: '',
      name: alarmNotificationProfileData.name,
      profile_type: alarmNotificationProfileData.profile_type,
      email: alarmNotificationProfileData.email.join(','),
      alarm_definition: alarmsDefSelected,
      alarm_instance: alarmsInstSelected,
      survindex: interceptAlarmSelected,
      frequency: alarmNotificationProfileData.frequency,
      last_mailing_time: alarmNotificationProfileData.last_mailing_time,
    }

    const payload = {
      data: adaptedAlarmNotificationProfileData,
      auditDetails: getAuditDetailsToSubmit(
        alarmNotificationProfileData,
        null,
        isAuditEnabled,
        userId,
        userName,
        'CREATE',
      ),
    };

    return payload;
  };

  const handleClickSave = () => {
    if (selectedRows?.length <= 0 || selectedRows?.length > 10) {
      showError(t('alarmNotificationProfile.form.grid.error'));
      return;
    }
    const payload = getCreateAlarmNotificationProfileActionPayload();
    if (payload) {
      createAlarmNotificationProfile(payload, history);
    }
  };

  const handleCancel = () => goToAlarmNotificationProfileList(history);

  const getInterceptAlarmListByParam = (body: any) : AxiosPromise<ServerResponse<InterceptAlarm>> => {
    return alarmNotificationApi.getInterceptAlarmList(body);
  }

  const getAlarmInstListByParam = (body: any): AxiosPromise<ServerResponse<AlarmsInst>> => {
    return alarmNotificationApi.getAlarmsInstList(body);
  }

  const renderForm = () => {
    if (formData && template && formGridData) {
      return (
        <AlarmNotificationProfileForm
          ref={formRef}
          data={formData}
          template={template}
          onFieldUpdate={handleFieldUpdate}
          onSelectionChanged={handleSelectionChanged}
          onFieldCreateNew={handleFieldCreateNew}
          isEditing
          gridData={formGridData}
          profileType={currentContentType}
          getAlarmIntercept={getInterceptAlarmListByParam}
          getAlarmInstace={getAlarmInstListByParam}
          alarmDefData={addIdToAlarmDef(['alarmId'], alarmsDefList)}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="AlarmNotificationProfileCreate">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('alarmNotificationProfile.create.title'))}
          {renderForm()}
        </FormLayoutContent>
        <FormLayoutFooter>{renderFormPanel('')}</FormLayoutFooter>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(AlarmNotificationProfileCreate));
